import { KeyboardEventHandler, useEffect, useRef, useState } from "react";
import { CheckCircle2, Loader2, Database, Lightbulb, Package, Layers, Filter, BarChart2, Wrench, Sparkles, Brain, ShieldCheck, Search, Copy, Table2, TrendingUp, PieChart, Gauge, MapPin, LayoutGrid } from "lucide-react";

import { Textarea, Label } from "@/components/atoms";
import { WidgetPreviewModal } from "./WidgetPreviewModal";
import { ClarificationPanel } from "./clarification/ClarificationPanel";
import type { ClarificationItemShape, ClarificationAnswer } from "./clarification/types";

function renderInline(text: string): React.ReactNode {
    const parts = text.split(/\*\*(.*?)\*\*/g);
    if (parts.length === 1) return text;
    return (
        <>
            {parts.map((part, i) =>
                i % 2 === 1 ? <strong key={i} className="font-semibold">{part}</strong> : part
            )}
        </>
    );
}

function AgentFeedbackBody({ message }: { message: string }) {
    const normalized = message
        .replace(/\s(\d+)\.\s/g, (_, n) => `\n${n}. `)
        .trim();

    const blocks = normalized.split(/\n{2,}/).map((b) => b.trim()).filter(Boolean);

    type Segment =
        | { kind: "prose"; text: string }
        | { kind: "heading"; text: string }
        | { kind: "ordered-list"; items: string[] }
        | { kind: "unordered-list"; items: string[] };

    const segments: Segment[] = [];

    for (const block of blocks) {
        const lines = block.split("\n").map((l) => l.trim()).filter(Boolean);
        let orderedList: string[] = [];
        let unorderedList: string[] = [];

        const flush = () => {
            if (orderedList.length > 0) { segments.push({ kind: "ordered-list", items: orderedList }); orderedList = []; }
            if (unorderedList.length > 0) { segments.push({ kind: "unordered-list", items: unorderedList }); unorderedList = []; }
        };

        for (const line of lines) {
            if (/^\d+\.\s/.test(line)) {
                if (unorderedList.length > 0) { segments.push({ kind: "unordered-list", items: unorderedList }); unorderedList = []; }
                orderedList.push(line.replace(/^\d+\.\s*/, ""));
            } else if (/^[-*]\s/.test(line)) {
                if (orderedList.length > 0) { segments.push({ kind: "ordered-list", items: orderedList }); orderedList = []; }
                unorderedList.push(line.replace(/^[-*]\s*/, ""));
            } else {
                flush();
                // A line that is purely **text** (optional trailing colon) is a section heading
                if (/^\*\*[^*]+\*\*:?$/.test(line)) {
                    segments.push({ kind: "heading", text: line.replace(/^\*\*|\*\*:?$/g, "") });
                } else {
                    segments.push({ kind: "prose", text: line });
                }
            }
        }
        flush();
    }

    return (
        <div className="space-y-2 text-sm text-[#2d3a3f]">
            {segments.map((seg, i) => {
                if (seg.kind === "heading") {
                    return (
                        <p key={i} className="font-semibold text-[#079a95]">{seg.text}</p>
                    );
                }
                if (seg.kind === "ordered-list") {
                    return (
                        <ul key={i} className="space-y-1 pl-1">
                            {seg.items.map((item, j) => {
                                const [name, desc] = item.split(" — ");
                                return (
                                    <li key={j} className="flex items-start gap-2 leading-snug">
                                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#079a95]" />
                                        {desc ? (
                                            <span>
                                                <span className="font-semibold">{renderInline(name)}</span>
                                                <span className="text-[#5a7a84]"> — {renderInline(desc)}</span>
                                            </span>
                                        ) : (
                                            <span>{renderInline(item)}</span>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    );
                }
                if (seg.kind === "unordered-list") {
                    return (
                        <ul key={i} className="space-y-1 pl-1">
                            {seg.items.map((item, j) => {
                                const colonIdx = item.indexOf(":");
                                const hasLabel = colonIdx > 0 && colonIdx < item.length - 1;
                                return (
                                    <li key={j} className="flex items-start gap-2 leading-snug">
                                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#079a95]" />
                                        {hasLabel ? (
                                            <span>
                                                <span className="font-semibold">{renderInline(item.slice(0, colonIdx))}</span>
                                                <span className="text-[#5a7a84]">{renderInline(item.slice(colonIdx))}</span>
                                            </span>
                                        ) : (
                                            <span>{renderInline(item)}</span>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    );
                }
                // prose
                return (
                    <p
                        key={i}
                        className={seg.text.replace(/\*\*/g, "").endsWith(":") ? "font-semibold text-[#079a95]" : "leading-snug"}
                    >
                        {renderInline(seg.text)}
                    </p>
                );
            })}
        </div>
    );
}
import { useAuth } from "@/modules/auth";
import { canPublishGlobally } from "@/modules/widgets/utils/permissions";

type ProgressUpdate = {
    type: string;
    message: string;
};

function StepIcon({ type, isActive }: { type: string; isActive: boolean }) {
    if (isActive) return <Loader2 className="mt-0.5 h-3.5 w-3.5 shrink-0 animate-spin text-[#079a95]" />;
    switch (type) {
        case "KNOWLEDGE_BASE_QUERY":  return <Database     className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#079a95]" />;
        case "PLANNING":              return <Lightbulb    className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#079a95]" />;
        case "INFERRING_PRODUCT":     return <Package      className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#079a95]" />;
        case "INFERRING_LEVEL":       return <Layers       className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#079a95]" />;
        case "INFERRING_SEGMENT":     return <Filter       className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#079a95]" />;
        case "RESOLVING_METRICS":     return <BarChart2    className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#079a95]" />;
        case "INFERRING_FILTERS":     return <Filter       className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#079a95]" />;
        case "VALIDATION":            return <ShieldCheck  className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#079a95]" />;
        case "TOOL_INVOCATION":       return <Wrench       className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#079a95]" />;
        case "WIDGET_CREATING":       return <Sparkles     className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#079a95]" />;
        case "THOUGHT":               return <Brain        className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#079a95]" />;
        case "FINDING_EXAMPLES":      return <Search       className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#079a95]" />;
        default:                      return <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#079a95]" />;
    }
}

export type { ClarificationAnswer };

interface ExamplePreviewShape {
    title: string;
    type?: string;
    chartType?: string;
    service?: { parameters?: { level?: string; segment?: string } };
    metrics?: string[];
    [key: string]: any;
}

function getWidgetTypeIcon(type?: string, chartType?: string) {
    if (type === "table" || type === "competitor-table") {
        return <Table2 className="h-4 w-4 text-[#079a95] shrink-0" />;
    }
    if (type === "ticker") {
        return <Gauge className="h-4 w-4 text-[#079a95] shrink-0" />;
    }
    if (type === "table-map" || type === "audience-map") {
        return <MapPin className="h-4 w-4 text-[#079a95] shrink-0" />;
    }
    if (type === "combined") {
        return <LayoutGrid className="h-4 w-4 text-[#079a95] shrink-0" />;
    }
    if (type === "chart") {
        switch (chartType) {
            case "line":
                return <TrendingUp className="h-4 w-4 text-[#079a95] shrink-0" />;
            case "pie":
            case "doughnut":
                return <PieChart className="h-4 w-4 text-[#079a95] shrink-0" />;
            default:
                return <BarChart2 className="h-4 w-4 text-[#079a95] shrink-0" />;
        }
    }
    return <BarChart2 className="h-4 w-4 text-[#079a95] shrink-0" />;
}

function getWidgetTypeLabel(type?: string, chartType?: string): string {
    if (type === "table") return "Table";
    if (type === "competitor-table") return "Competitor Table";
    if (type === "ticker") return "Ticker";
    if (type === "table-map") return "Table Map";
    if (type === "audience-map") return "Audience Map";
    if (type === "combined") return "Combined";
    if (type === "chart") {
        switch (chartType) {
            case "line": return "Line Chart";
            case "bar": return "Bar Chart";
            case "stack-bar": return "Stacked Bar Chart";
            case "stacked-bar-line": return "Stacked Bar + Line";
            case "pie": return "Pie Chart";
            case "doughnut": return "Donut Chart";
            case "funnel": return "Funnel Chart";
            case "bubble": return "Bubble Chart";
            default: return "Chart";
        }
    }
    return type ?? "Widget";
}

function ClarificationWidgetPreviewCard({ widget }: { widget: Record<string, any> }) {
    const [isModalOpen, setIsModalOpen] = useState(false);

    const type: string | undefined = widget.type;
    const chartType: string | undefined = widget.chartType;
    const title: string | undefined = widget.title;
    const level: string | undefined = widget.service?.parameters?.level;
    const segment: string | undefined = widget.service?.parameters?.segment;
    const metrics: string[] | undefined = widget.metrics;

    // Collect data sources / products from the widget spec
    const dataSources: string[] = [];
    const rawSources = widget.service?.dataSources;
    if (Array.isArray(rawSources)) {
        for (const ds of rawSources) {
            const name = ds?.service ?? ds?.name ?? ds?.product ?? ds?.productName;
            if (name && !dataSources.includes(name)) dataSources.push(String(name));
        }
    }
    // Also check top-level products array (some specs have this)
    const rawProducts = widget.products ?? widget.service?.parameters?.products;
    if (Array.isArray(rawProducts)) {
        for (const p of rawProducts) {
            const name = typeof p === "string" ? p : (p?.productName ?? p?.name ?? p?.service);
            if (name && !dataSources.includes(name)) dataSources.push(String(name));
        }
    }

    const subtitleParts: string[] = [];
    if (level) subtitleParts.push(`${level} level`);
    if (segment) subtitleParts.push(`by ${segment}`);

    return (
        <>
            <div className="rounded-lg border border-[#079a95]/20 bg-white/60 p-3 space-y-2">
                <div className="flex items-center gap-2">
                    {getWidgetTypeIcon(type, chartType)}
                    <span className="text-xs font-semibold text-[#079a95]">
                        {getWidgetTypeLabel(type, chartType)}
                    </span>
                </div>
                {title && (
                    <p className="text-[13px] font-medium text-[#2d3a3f] leading-snug">{title}</p>
                )}
                {subtitleParts.length > 0 && (
                    <p className="text-[11px] text-[#5a717a]">{subtitleParts.join(" · ")}</p>
                )}
                {dataSources.length > 0 && (
                    <p className="text-[11px] text-[#5a717a]">
                        <span className="font-medium">Products:</span>{" "}
                        {dataSources.slice(0, 4).join(", ")}
                        {dataSources.length > 4 ? ` +${dataSources.length - 4} more` : ""}
                    </p>
                )}
                {metrics && metrics.length > 0 && (
                    <div className="flex flex-wrap gap-1 pt-0.5">
                        {metrics.slice(0, 5).map((m) => (
                            <span
                                key={m}
                                className="inline-block rounded-full bg-[#079a95]/10 px-2 py-0.5 text-[10px] font-medium text-[#079a95]"
                            >
                                {m}
                            </span>
                        ))}
                        {metrics.length > 5 && (
                            <span className="inline-block rounded-full bg-[#079a95]/10 px-2 py-0.5 text-[10px] font-medium text-[#079a95]">
                                +{metrics.length - 5} more
                            </span>
                        )}
                    </div>
                )}
                <div className="pt-1">
                    <button
                        type="button"
                        onClick={() => setIsModalOpen(true)}
                        className="w-full cursor-pointer rounded-md border border-[#079a95]/40 bg-white px-3 py-1.5 text-[11px] font-medium text-[#079a95] transition-colors hover:bg-[#e8f5f4]"
                    >
                        Preview
                    </button>
                </div>
            </div>

            {isModalOpen && (
                <WidgetPreviewModal
                    widget={widget as any}
                    onConfirm={() => {}}
                    onClose={() => setIsModalOpen(false)}
                    forceEnableConfirm={false}
                />
            )}
        </>
    );
}

interface AddWidgetAIPanelProps {
    prompt: string;
    onPromptChange: (value: string) => void;
    onPromptKeyDown: KeyboardEventHandler<HTMLTextAreaElement | HTMLInputElement>;
    onSubmit: () => void;
    isSubmitDisabled: boolean;
    publishToAll: boolean;
    onPublishToAllChange: (value: boolean) => void;
    feedbackMessage?: string;
    feedbackType?: "success" | "error" | "info";
    clarificationItems?: ClarificationItemShape[];
    onClarificationConfirm?: (answers: ClarificationAnswer[]) => void;
    onFilterReject?: () => void;
    examplePreview?: ExamplePreviewShape;
    onExamplePreviewOpen?: () => void;
    onExamplePreviewAccept?: () => void;
    onExamplePreviewDiscard?: () => void;
    isCreating: boolean;
    isProcessing: boolean;
    progressUpdates: ProgressUpdate[];
    refinementPrompt?: string;
    onRefinementPromptChange?: (value: string) => void;
    onRefinementKeyDown?: KeyboardEventHandler<HTMLTextAreaElement | HTMLInputElement>;
}

export function AddWidgetAIPanel({
    prompt,
    onPromptChange,
    onPromptKeyDown,
    onSubmit,
    feedbackMessage,
    feedbackType,
    clarificationItems,
    onClarificationConfirm,
    onFilterReject,
    examplePreview,
    onExamplePreviewOpen,
    onExamplePreviewAccept,
    onExamplePreviewDiscard,
    isCreating,
    isProcessing,
    progressUpdates,
    publishToAll,
    onPublishToAllChange,
    refinementPrompt,
    onRefinementPromptChange,
    onRefinementKeyDown,
}: AddWidgetAIPanelProps) {
    const showProgress = isCreating || isProcessing;
    const [isRefineMode, setIsRefineMode] = useState(false);
    const [inlineRefineValue, setInlineRefineValue] = useState("");
    const allUpdates = [
        { type: "PLANNING" as const, message: "Generating widget preview..." },
        ...progressUpdates,
    ];
    const { user } = useAuth();
    const scrollRef = useRef<HTMLDivElement>(null);


    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [allUpdates.length]);

    // Exit refine mode whenever a new preview arrives
    useEffect(() => {
        if (examplePreview) {
            setIsRefineMode(false);
            setInlineRefineValue("");
        }
    }, [examplePreview]);
    const canPublish = canPublishGlobally(user);

    return (
        <div className="space-y-4">

            {/* ── 1. Progress log (while generating) ── */}
            {showProgress && (
                <section className="rounded-xl border border-[#e0e0e0] bg-[#f5f7f8] p-4">
                    <div className="text-[12px] font-medium text-[#2d3a3f]">Generating preview...</div>
                    <div ref={scrollRef} className="mt-3 max-h-48 space-y-2.5 overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                        {allUpdates.map((update, index) => {
                            const isActive = index === allUpdates.length - 1;
                            return (
                                <div key={`${update.type}-${index}`} className={`flex items-start gap-2 text-[12px] transition-opacity ${isActive ? "opacity-100" : "opacity-60"}`}>
                                    <StepIcon type={update.type} isActive={isActive} />
                                    <span className={isActive ? "font-medium text-[#2d3a3f]" : "text-[#5a717a]"}>
                                        {update.message}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </section>
            )}

            {/* ── 3. Proposed widget preview ── */}
            {examplePreview && (
                <section className="rounded-xl border border-[#079a95]/40 bg-[#079a95]/5 p-4 space-y-3">
                    <div className="flex items-center gap-2">
                        <Copy className="h-4 w-4 text-[#079a95] shrink-0" />
                        <span className="text-xs font-semibold uppercase tracking-wide text-[#079a95]">
                            Proposed widget
                        </span>
                    </div>

                    {isRefineMode ? (
                        <>
                            <Textarea
                                value={inlineRefineValue}
                                onChange={(e) => {
                                    setInlineRefineValue(e.target.value);
                                    onRefinementPromptChange?.(e.target.value);
                                }}
                                onKeyDown={onRefinementKeyDown}
                                placeholder="E.g. Make it a line chart, add the conversions metric…"
                                disabled={isCreating || isProcessing}
                                className="min-h-[80px]"
                            />
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setIsRefineMode(false);
                                        setInlineRefineValue("");
                                        onRefinementPromptChange?.("");
                                    }}
                                    className="cursor-pointer rounded-lg border border-[#c9d1d4] bg-white px-3 py-1.5 text-xs font-medium text-[#2d3a3f] transition-colors hover:bg-[#f5f7f8]"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (inlineRefineValue.trim()) onSubmit?.();
                                    }}
                                    disabled={!inlineRefineValue.trim() || isCreating || isProcessing}
                                    className="flex-1 cursor-pointer rounded-lg bg-[#079a95] px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-[#067a76] disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Refine
                                </button>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="space-y-0.5">
                                <p className="font-medium text-[14px] text-[#2d3a3f]">{examplePreview.title}</p>
                                <p className="text-[12px] text-[#5a717a]">
                                    {[
                                        examplePreview.type,
                                        examplePreview.chartType,
                                        examplePreview.service?.parameters?.level ? `${examplePreview.service.parameters.level} level` : null,
                                        examplePreview.service?.parameters?.segment ? `by ${examplePreview.service.parameters.segment}` : null,
                                    ].filter(Boolean).join(" · ")}
                                </p>
                                {examplePreview.metrics?.length ? (
                                    <p className="text-[12px] text-[#5a717a]">
                                        {examplePreview.metrics.slice(0, 4).join(", ")}
                                        {examplePreview.metrics.length > 4 ? ` +${examplePreview.metrics.length - 4} more` : ""}
                                    </p>
                                ) : null}
                            </div>
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={() => setIsRefineMode(true)}
                                    className="cursor-pointer rounded-lg border border-[#c9d1d4] bg-white px-3 py-1.5 text-xs font-medium text-[#2d3a3f] transition-colors hover:bg-[#f5f7f8]"
                                >
                                    Refine
                                </button>
                                <button
                                    type="button"
                                    onClick={onExamplePreviewOpen}
                                    className="flex-1 cursor-pointer rounded-lg border border-[#079a95] bg-white px-3 py-1.5 text-xs font-medium text-[#079a95] transition-colors hover:bg-[#e8f5f4]"
                                >
                                    Preview
                                </button>
                            </div>
                        </>
                    )}
                </section>
            )}

            {/* ── 4. Clarification questions ── */}
            {clarificationItems && clarificationItems.length > 0 && (
                <ClarificationPanel
                    items={clarificationItems}
                    onConfirm={(answers) => onClarificationConfirm?.(answers)}
                    onFilterReject={onFilterReject}
                    renderMessageBody={(item) => <AgentFeedbackBody message={item.message} />}
                    renderPreviewCard={(item) =>
                        item.previewWidget ? <ClarificationWidgetPreviewCard widget={item.previewWidget} /> : null
                    }
                />
            )}

            {/* ── 5. Plain feedback / error ── */}
            {feedbackMessage && feedbackType !== "info" && (
                <div className={`rounded-xl border px-4 py-3 ${
                    feedbackType === "success"
                        ? "border-[#0f9c66]/30 bg-[#0f9c66]/5"
                        : "border-[#d14343]/30 bg-[#d14343]/5"
                }`}>
                    <p className={`text-sm font-medium ${feedbackType === "success" ? "text-[#0f9c66]" : "text-[#d14343]"}`}>
                        {feedbackMessage}
                    </p>
                </div>
            )}

            {/* ── 6. Visibility toggle ── */}
            {canPublish ? (
                <section className="rounded-[24px] bg-white">
                    <div className="flex items-center justify-between">
                        <div>
                            <h4 className="font-roboto font-medium text-[14px] text-[#2d3a3f]">Visible to Everybody</h4>
                            <p className="text-[12px] text-[#5a717a]">
                                {publishToAll ? "Anybody with access to the dashboard will see" : "Visible to admins only"}
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={() => onPublishToAllChange(!publishToAll)}
                            className={`cursor-pointer inline-flex h-6 w-12 items-center rounded-full transition-colors ${publishToAll ? "bg-[#2d3a3f]" : "bg-[#c9d1d4]"}`}
                            aria-pressed={publishToAll}
                        >
                            <span
                                className={`ml-0.5 inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${publishToAll ? "translate-x-6" : "translate-x-0"}`}
                            />
                        </button>
                    </div>
                </section>
            ) : null}

            {/* ── 7. Prompt input (always at the bottom) ── */}
            {examplePreview ? (
                <section className="rounded-[24px] bg-white">
                    <Label text="Your request">
                        <div className="flex items-start gap-2 rounded-lg border border-[#e0e8ea] bg-[#f5f7f8] px-3 py-2.5">
                            <p className="flex-1 text-sm text-[#5a717a] leading-snug break-words">{prompt}</p>
                            <button
                                type="button"
                                onClick={() => navigator.clipboard.writeText(prompt)}
                                title="Copy"
                                className="mt-0.5 cursor-pointer shrink-0 text-[#9ab0b8] hover:text-[#079a95] transition-colors"
                            >
                                <Copy className="h-3.5 w-3.5" />
                            </button>
                        </div>
                    </Label>
                </section>
            ) : (
                <section className="rounded-[24px] bg-white">
                    <Label text="Describe your widget">
                        <Textarea
                            value={prompt}
                            onChange={(event) => onPromptChange(event.target.value)}
                            onKeyDown={onPromptKeyDown}
                            placeholder="E.g., Create a bar chart showing click-through rate by device for the last 30 days..."
                            disabled={isCreating || isProcessing}
                        />
                    </Label>
                </section>
            )}

        </div>
    );
}
